import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { Aviso, AreaTexto, Botao, Campo, Entrada, Modal, Selecao } from '../../componentes/ui.jsx';
import './pets.css';

/**
 * Cadastro de pet, compartilhado pela ficha do contato e pelo assistente de
 * agendamento (o "+ novo pet" abre este mesmo modal). Um lugar so, para os dois
 * falarem a mesma lingua com a API.
 */

export const ESPECIES = {
  cao: { rotulo: 'Cão', icone: '🐶' },
  gato: { rotulo: 'Gato', icone: '🐱' },
  ave: { rotulo: 'Ave', icone: '🐦' },
  roedor: { rotulo: 'Roedor', icone: '🐹' },
  reptil: { rotulo: 'Réptil', icone: '🦎' },
  outro: { rotulo: 'Outro', icone: '🐾' }
};

export const PORTES = {
  mini: 'Mini',
  pequeno: 'Pequeno',
  medio: 'Médio',
  grande: 'Grande',
  gigante: 'Gigante'
};

export const iconeDaEspecie = (especie) => (ESPECIES[especie] ?? ESPECIES.outro).icone;

/** "3 anos", "8 meses" ou null (sem nascimento). Calculada na hora: nunca fica velha. */
export function idadeTexto(nascimento) {
  if (!nascimento) return null;
  const [a, m, d] = nascimento.split('-').map(Number);
  const hoje = new Date();
  let meses = (hoje.getFullYear() - a) * 12 + (hoje.getMonth() + 1 - m);
  if (hoje.getDate() < d) meses -= 1;
  if (meses < 0) return null;
  if (meses < 1) return 'recém-nascido';
  if (meses < 12) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  return `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
}

export const pesoTexto = (gramas) =>
  gramas ? `${(gramas / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg` : null;

/** "Golden · Grande · 3 anos": o que cabe numa linha sobre o pet. */
export function resumoDoPet(p) {
  return [p.raca, PORTES[p.porte], idadeTexto(p.nascimento)].filter(Boolean).join(' · ');
}

/** "🐶 Thor": o pet de uma OS, ou nada (OS antiga, sem pet, nao mostra "undefined"). */
export function PetDaOS({ a, className = 'pet-tag texto-fraco' }) {
  if (!a?.petNome) return null;
  return (
    <span className={className}>
      {iconeDaEspecie(a.petEspecie)} {a.petNome}
    </span>
  );
}

/** Lista compacta dos pets do tutor (painel da conversa, popup de perfil). */
export function PetsDoTutor({ pets = [] }) {
  if (pets.length === 0) return <p className="texto-fraco">Nenhum pet cadastrado.</p>;
  return (
    <ul className="pets-mini">
      {pets.map((p) => (
        <li key={p.id}>
          <span aria-hidden="true">{iconeDaEspecie(p.especie)}</span>
          <strong>{p.nome}</strong>
          <span className="texto-fraco">{resumoDoPet(p)}</span>
        </li>
      ))}
    </ul>
  );
}

const formVazio = {
  nome: '',
  especie: 'cao',
  raca: '',
  porte: '',
  sexo: '',
  nascimento: '',
  peso: '',
  observacoes: ''
};

function formDoPet(p) {
  return {
    nome: p.nome,
    especie: p.especie,
    raca: p.raca ?? '',
    porte: p.porte ?? '',
    sexo: p.sexo ?? '',
    nascimento: p.nascimento ?? '',
    peso: p.pesoGramas ? String(p.pesoGramas / 1000).replace('.', ',') : '',
    observacoes: p.observacoes ?? ''
  };
}

/**
 * @param {object} p
 * @param {string} p.leadId
 * @param {object} [p.pet]        quando vem, edita; senao cadastra
 * @param {(pet:object)=>void} [p.aoSalvar]
 */
export function ModalPet({ leadId, pet = null, aoFechar, aoSalvar }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(pet ? formDoPet(pet) : formVazio);
  const [erroLocal, setErroLocal] = useState(null);
  const muda = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const salvar = useMutation({
    mutationFn: (corpo) =>
      pet ? api.patch(`/api/pets/${pet.id}`, corpo) : api.post(`/api/leads/${leadId}/pets`, corpo),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['pets', leadId] });
      aoSalvar?.(r.pet);
    }
  });

  function enviar() {
    setErroLocal(null);
    if (!form.nome.trim()) return setErroLocal('Informe o nome do pet.');
    const kg = form.peso.trim() ? Number(form.peso.replace(',', '.')) : null;
    if (kg !== null && (!Number.isFinite(kg) || kg <= 0)) return setErroLocal('Peso inválido. Use algo como 4,5.');

    // Na edicao, campo limpo vira null (apaga); no cadastro, simplesmente nao vai.
    const vazio = pet ? null : undefined;
    salvar.mutate({
      nome: form.nome.trim(),
      especie: form.especie,
      raca: form.raca.trim(),
      porte: form.porte || vazio,
      sexo: form.sexo || vazio,
      nascimento: form.nascimento || vazio,
      pesoGramas: kg === null ? vazio : Math.round(kg * 1000),
      observacoes: form.observacoes.trim()
    });
  }

  const erros = salvar.error?.camposComErro ?? {};

  return (
    <Modal
      titulo={pet ? `Editar ${pet.nome}` : 'Novo pet'}
      aberto
      aoFechar={aoFechar}
      largura={560}
      rodape={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao carregando={salvar.isPending} onClick={enviar}>
            {pet ? 'Salvar' : 'Cadastrar pet'}
          </Botao>
        </>
      }
    >
      <div className="form-pet">
        {(erroLocal || salvar.isError) && <Aviso tom="perigo">{erroLocal ?? salvar.error.message}</Aviso>}

        <Campo rotulo="Nome" obrigatorio erro={erros.nome}>
          <Entrada autoFocus value={form.nome} maxLength={60} onChange={muda('nome')} placeholder="Thor" />
        </Campo>

        <div className="form-pet__duas">
          <Campo rotulo="Espécie" obrigatorio>
            <Selecao value={form.especie} onChange={muda('especie')}>
              {Object.entries(ESPECIES).map(([chave, e]) => (
                <option key={chave} value={chave}>
                  {e.icone} {e.rotulo}
                </option>
              ))}
            </Selecao>
          </Campo>
          <Campo rotulo="Raça" erro={erros.raca}>
            <Entrada value={form.raca} maxLength={60} onChange={muda('raca')} placeholder="SRD, Poodle..." />
          </Campo>
        </div>

        <div className="form-pet__duas">
          <Campo rotulo="Porte">
            <Selecao value={form.porte} onChange={muda('porte')}>
              <option value="">Não informado</option>
              {Object.entries(PORTES).map(([chave, rotulo]) => (
                <option key={chave} value={chave}>
                  {rotulo}
                </option>
              ))}
            </Selecao>
          </Campo>
          <Campo rotulo="Sexo">
            <Selecao value={form.sexo} onChange={muda('sexo')}>
              <option value="">Não informado</option>
              <option value="macho">Macho</option>
              <option value="femea">Fêmea</option>
            </Selecao>
          </Campo>
        </div>

        <div className="form-pet__duas">
          <Campo rotulo="Nascimento" erro={erros.nascimento} dica="Pode ser aproximado.">
            <Entrada
              type="date"
              value={form.nascimento}
              max={new Date().toLocaleDateString('sv-SE')}
              onChange={muda('nascimento')}
            />
          </Campo>
          <Campo rotulo="Peso (kg)">
            <Entrada value={form.peso} inputMode="decimal" onChange={muda('peso')} placeholder="4,5" />
          </Campo>
        </div>

        <Campo rotulo="Saúde e temperamento" dica="Alergias, medos, medicação, cuidados no banho...">
          <AreaTexto value={form.observacoes} maxLength={2000} onChange={muda('observacoes')} />
        </Campo>
      </div>
    </Modal>
  );
}
